import React, { useState } from "react";
import { Box, Typography, IconButton, Modal, CircularProgress } from "@mui/material";
import { useTheme } from "@mui/material";
import { useDispatch } from "react-redux";
import uploadImageIcon from "../../assets/images/upload-image-icon.svg";
import cancelSharpIcon from "../../assets/images/cancelSharpIcon.svg";
import ImageCropper from "../../components/CommonComponents/ImageCroper";
import { uploadImage } from "../../store/reducers/authentication/apiThunk";
import { ToastNotifyError } from "../../components/CommonComponents/Toast/ToastNotify";
import rollbar from "../../rollbarConfig";

const MediaSection = ({
  productMedia,
  setProductMedia,
  setValue,
  handleRemoveMedia,
}) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const [openCropperModal, setOpenCropperModal] = useState(false);
  const [currentImageUrl, setCurrentImageUrl] = useState(null);
  const [isUploadingMedia, setIsUploadingMedia] = useState(false);

  const handleMediaUpload = (event) => {
    const files = Array.from(event.target.files);
    if (files?.length === 0) return;

    const maxSize = 5 * 1024 * 1024;
    const file = files[0];
    
    if (file?.size > maxSize) {
      ToastNotifyError("File size must be less than 5MB");
      event.target.value = "";
      return;
    }

    if (productMedia?.length >= 250) {
      ToastNotifyError("You can upload up to 250 media files");
      event.target.value = "";
      return;
    }

    setCurrentImageUrl(URL.createObjectURL(file));
    setOpenCropperModal(true);
    event.target.value = "";
  };

  const handleMediaCrop = async ({ file }) => {
    setIsUploadingMedia(true);
    try {
      const response = await dispatch(uploadImage(file));
      const { key, uploadResponse } = response?.payload;
      const { status } = uploadResponse || {};
      
      if (status === 200) {
        const finalImageUrl = process.env.REACT_APP_S3_IMAGES_URL + key;
        
        const updatedMedia = [...productMedia, finalImageUrl];
        setProductMedia(updatedMedia);
      } else {
        ToastNotifyError("Failed to upload media");
      }
    } catch (err) {
      rollbar.error("Error uploading media", err);
      ToastNotifyError("Failed to upload media");
    } finally {
      setIsUploadingMedia(false);
      setOpenCropperModal(false);
      setCurrentImageUrl(null);
    }
  };

  const handleCancelCropper = () => {
    setOpenCropperModal(false);
    setCurrentImageUrl(null);
  };

  return (
    <Box sx={{ mt: 3, mb: 3 }}>
      <Typography sx={{
        fontFamily: 'Lexend',
        fontSize: '14px',
        fontWeight: 500,
        mb: 1.5,
        color: theme.palette?.primary?.main || '#0F0F0F',
      }}>
        Media
      </Typography>
      
      <Box
        component="label"
        htmlFor="product-media-upload"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          p: 3,
          border: `1px dashed ${theme.palette?.customColor?.secondaryThird || "#E0E0E0"}`,
          borderRadius: '4px',
          cursor: productMedia?.length >= 250 ? 'not-allowed' : 'pointer',
          mb: productMedia?.length > 0 ? 2 : 0,
          backgroundColor: productMedia?.length >= 250 ? theme.palette?.action?.disabledBackground || 'rgba(0, 0, 0, 0.02)' : 'transparent',
          '&:hover': {
            borderColor: productMedia?.length >= 250 ? theme.palette?.customColor?.secondaryThird || "#E0E0E0" : theme.palette?.primary?.main || '#0F0F0F',
            backgroundColor: productMedia?.length >= 250 ? theme.palette?.action?.disabledBackground || 'rgba(0, 0, 0, 0.02)' : theme.palette?.action?.hover || 'rgba(0, 0, 0, 0.04)',
          }
        }}
      >
        <img src={uploadImageIcon} alt='upload' width='24px' style={{ marginBottom: '8px' }} />
        <Typography sx={{
          fontFamily: 'Lexend',
          fontSize: '14px',
          fontWeight: 400,
          color: theme.palette?.primary?.main || '#0F0F0F',
          mb: 0.5,
        }}>
          Upload Media
        </Typography>
        <Typography sx={{
          fontFamily: 'Lexend',
          fontSize: '12px',
          fontWeight: 400,
          color: theme.palette?.text?.secondary || '#656565',
        }}>
          Supported format: .png, .jpg
        </Typography>
        <input
          type="file"
          id="product-media-upload"
          accept="image/png,image/jpeg,image/jpg"
          style={{ display: 'none' }}
          onChange={handleMediaUpload}
          disabled={productMedia?.length >= 250}
        />
      </Box>

      {productMedia?.length > 0 && (
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 2 }}>
          {productMedia?.map((mediaUrl, mediaIndex) => (
            <Box
              key={mediaIndex}
              sx={{
                position: 'relative',
                width: '82px',
                height: '82px',
                border: `1.22px solid ${theme.palette?.customColor?.secondaryThird || "#E0E0E0"}`,
                overflow: 'hidden',
                flexShrink: 0,
              }}
            >
              <img
                src={mediaUrl}
                alt={`Media ${mediaIndex + 1}`}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                }}
              />
              <IconButton
                size="small"
                onClick={() => handleRemoveMedia(mediaIndex)}
                sx={{
                  position: 'absolute',
                  top: 4,
                  right: 4,
                  width: '24px',
                  height: '24px',
                  minWidth: '24px',

                  padding: 0,
                }}
              >
                <img src={cancelSharpIcon} alt='close' width='24px' />
              </IconButton>
            </Box>
          ))}
        </Box>
      )}

      <Modal
        open={openCropperModal}
        onClose={handleCancelCropper}
        closeAfterTransition
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backdropFilter: "blur(3px)",
        }}
      >
        <Box
          sx={{
            position: 'relative',
            backgroundColor: 'white',
            borderRadius: '8px',
            p: 2,
            maxWidth: '90vw',
            maxHeight: '90vh',
            overflow: 'auto',
          }}
        >
          <IconButton
            onClick={handleCancelCropper}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              zIndex: 1,
            }}
          >
            <img src={cancelSharpIcon} alt='close' width='20px' />
          </IconButton>
          {currentImageUrl && (
            <ImageCropper
              src={currentImageUrl}
              aspectRatio={1}
              onCancel={handleCancelCropper}
              onCropComplete={handleMediaCrop}
            />
          )}
          {isUploadingMedia && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                borderRadius: '8px',
              }}
            >
              <CircularProgress />
            </Box>
          )}
        </Box>
      </Modal>
    </Box>
  );
};

export default MediaSection;

